import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import CategorySelector from '../../../common/CategorySelector';
import CompanySelector from '../../../common/CompanySelector';
import './AddProductModal.scss';

interface ProductFormData {
  name: string;
  brand: string;
  amazon_asin: string;
  dimensions: string;
  base_unit: string;
  color: string;
  category: string[];
  description: string;
  is_active: boolean;
  image_file?: string;
  image_filename?: string;
  company_specific_codes?: Array<{ company_id: string; company_name: string; code: string }>;
}

interface VariantFormData {
  color: string;
  size: string;
  amazon_asin: string;
}

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (data?: any) => void;
  companyId?: string;
  companyName?: string;
}

interface ExcelProductData {
  name: string;
  brand: string;
  amazon_asin: string;
  dimensions: string;
  base_unit: string;
  color: string;
  category: string;
  description: string;
  is_active: string;
}

const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  companyId,
  companyName
}) => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [formData, setFormData] = useState<ProductFormData>({
    name: '',
    brand: '',
    amazon_asin: '',
    dimensions: '',
    base_unit: 'kg',
    color: '',
    category: [],
    description: '',
    is_active: isAdmin, // Default to true only for admins, others pending
    image_file: undefined,
    image_filename: undefined,
    company_specific_codes: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'single' | 'excel'>('single');
  const [excelData, setExcelData] = useState<ExcelProductData[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [editedData, setEditedData] = useState<ExcelProductData>({} as ExcelProductData);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Variant states
  const [variants, setVariants] = useState<VariantFormData[]>([]);
  const [showVariantForm, setShowVariantForm] = useState(false);

  // Company codes states
  const [selectedCompanies, setSelectedCompanies] = useState<Array<{ company_id: string; company_name: string }>>([]);
  const [companyCodes, setCompanyCodes] = useState<Record<string, string>>({});

  const baseUnits = ['kg', 'gr', 'lt', 'ml', 'metre', 'mm', 'adet', 'kutu', 'paket'];

  // Auto-select company if companyId is provided
  useEffect(() => {
    const fetchCompanyAndSelect = async () => {
      if (!isOpen || !companyId || !token) return;

      if (companyName) {
        // If we have both ID and Name, just set it
        setSelectedCompanies([{ company_id: companyId, company_name: companyName }]);
        return;
      }

      // If we have ID but no Name, fetch it
      try {
        console.log('🔍 MODAL DEBUG: Fetching company name for ID:', companyId);
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const fetchedName = data.data.name || 'Unknown Company';
            console.log('✅ MODAL DEBUG: Fetched company name:', fetchedName);
            setSelectedCompanies([{ company_id: companyId, company_name: fetchedName }]);
            return;
          }
        }

        // Fallback if fetch fails or data structure mismatch
        console.warn('⚠️ MODAL DEBUG: Failed to fetch company name, using fallback');
        setSelectedCompanies([{ company_id: companyId, company_name: 'Current Company' }]);
      } catch (error) {
        console.error('Error fetching company name:', error);
        // Fallback on error
        setSelectedCompanies([{ company_id: companyId, company_name: 'Current Company' }]);
      }
    };

    fetchCompanyAndSelect();
  }, [companyId, companyName, isOpen, token]);

  // Reset all form states
  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      amazon_asin: '',
      dimensions: '',
      base_unit: 'kg',
      color: '',
      category: [],
      description: '',
      is_active: isAdmin, // Default to true only for admins
      image_file: undefined,
      image_filename: undefined,
      company_specific_codes: []
    });
    setSelectedCompanies([]);
    setCompanyCodes({});
    setVariants([]);
    setErrors({});
    setActiveTab('single');
    setExcelData([]);
    setShowPreview(false);
    setEditingRow(null);
    setEditedData({} as ExcelProductData);
    setSelectedRows(new Set());
  };

  // Handle modal close with reset
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const downloadTemplate = () => {
    const headers = ['name', 'brand', 'amazon_asin', 'dimensions', 'base_unit', 'color', 'category', 'description', 'is_active'];
    const csvContent = [
      headers.join(','),
      'Test Product,Test Brand,B123456789,10x20x30 cm,adet,Blue,44000000,Test product description,true',
      'Sample Product,Sample Brand,C987654321,15x25x35 cm,kg,Red,39000000;42000000,Sample product description,true',
      'Demo Product,Demo Brand,D456789123,20x30x40 cm,lt,Green,56000000,Demo product description,false'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'products_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target?.result as string;
      const lines = csv.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const data: ExcelProductData[] = [];
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim()) {
          const values = lines[i].split(',').map(v => v.trim());
          const row: ExcelProductData = {
            name: values[0] || '',
            brand: values[1] || '',
            amazon_asin: values[2] || '',
            dimensions: values[3] || '',
            base_unit: values[4] || 'kg',
            color: values[5] || '',
            category: values[6] || '',
            description: values[7] || '',
            is_active: values[8] || 'true'
          };
          data.push(row);
        }
      }

      setExcelData(data);
      setShowPreview(true);
    };
    reader.readAsText(file);
  };

  const handleBulkCreate = async () => {
    setIsSubmitting(true);
    let successCount = 0;
    let errorCount = 0;

    for (const productData of excelData) {
      try {
        const productPayload = {
          ...productData,
          category: productData.category ? productData.category.split(',').map((cat: string) => cat.trim()).filter(Boolean) : [],
          is_active: productData.is_active === 'true' || productData.is_active === '1'
        };

        const token = localStorage.getItem('token');
        const response = await fetch(API_ENDPOINTS.PRODUCTS_V2, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(productPayload)
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    setIsSubmitting(false);

    if (successCount > 0) {
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    }

    alert(`Bulk creation completed: ${successCount} successful, ${errorCount} failed`);
  };

  const handleEditRow = (index: number) => {
    setEditingRow(index);
    setEditedData(excelData[index]);
  };

  const handleSaveRow = () => {
    if (editingRow !== null) {
      const newData = [...excelData];
      newData[editingRow] = editedData;
      setExcelData(newData);
      setEditingRow(null);
      setEditedData({} as ExcelProductData);
    }
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditedData({} as ExcelProductData);
  };

  const handleDeleteRow = (index: number) => {
    const newData = excelData.filter((_, i) => i !== index);
    setExcelData(newData);
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  const handleSelectRow = (index: number) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.size === excelData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(excelData.map((_, index) => index)));
    }
  };

  const handleDeleteSelected = () => {
    const newData = excelData.filter((_, index) => !selectedRows.has(index));
    setExcelData(newData);
    setSelectedRows(new Set());
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }

    if (!formData.category || formData.category.length === 0) {
      newErrors.category = 'At least one category is required';
    }

    if (!formData.base_unit.trim()) {
      newErrors.base_unit = 'Base unit is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        image_file: 'Please select an image file'
      }));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({
        ...prev,
        image_file: 'File size must be less than 5MB'
      }));
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setFormData(prev => ({
        ...prev,
        image_file: result,
        image_filename: file.name
      }));

      // Clear error
      if (errors.image_file) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.image_file;
          return newErrors;
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (field: keyof ProductFormData, value: string | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Size options for variants
  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  // Variant handlers
  const handleAddVariant = () => {
    setVariants([...variants, { color: '', size: '', amazon_asin: '' }]);
  };

  const handleRemoveVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const handleVariantChange = (index: number, field: keyof VariantFormData, value: string) => {
    const updatedVariants = [...variants];
    updatedVariants[index][field] = value;
    setVariants(updatedVariants);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('🔍 SUBMIT DEBUG: handleSubmit called');
    console.log('🔍 SUBMIT DEBUG: formData:', formData);

    if (!validateForm()) {
      console.log('🔍 SUBMIT DEBUG: Validation failed, errors:', errors);
      return;
    }

    console.log('🔍 SUBMIT DEBUG: Validation passed, submitting...');
    setIsSubmitting(true);

    try {
      // Build company_specific_codes from selectedCompanies and companyCodes
      const companySpecificCodes = selectedCompanies.map(company => ({
        company_id: company.company_id,
        company_name: company.company_name,
        code: companyCodes[company.company_id] || ''
      }));

      const productData = {
        ...formData,
        company_specific_codes: companySpecificCodes
      };

      console.log('🔍 DEBUG: Sending product data:', productData);
      const response = await fetch(API_ENDPOINTS.PRODUCTS_V2, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const mainProduct = result.data;
        const variantId = mainProduct.variant_id; // Backend generates this

        // If variants exist, create them with the same variant_id
        if (variants.length > 0 && variantId) {
          console.log('🔍 DEBUG: Creating variants with variant_id:', variantId);

          for (const variant of variants) {
            const variantData = {
              ...formData,
              color: variant.color || formData.color,
              dimensions: variant.size || formData.dimensions, // Map size to dimensions
              amazon_asin: variant.amazon_asin || formData.amazon_asin,
              variant_id: variantId, // Use same variant_id
              // Image will be same (image_gridfs_id linked automatically)
            };

            try {
              const variantResponse = await fetch(API_ENDPOINTS.PRODUCTS_V2, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(variantData)
              });

              if (!variantResponse.ok) {
                console.error('Failed to create variant:', variant);
              }
            } catch (error) {
              console.error('Error creating variant:', error);
            }
          }
        }

        // Reset form
        setFormData({
          name: '',
          brand: '',
          amazon_asin: '',
          dimensions: '',
          base_unit: 'kg',
          color: '',
          category: [],
          description: '',
          is_active: true
        });
        setErrors({});
        setVariants([]);

        // Call success callback
        if (onSuccess) {
          onSuccess(result.data);
        }

        handleClose();
      } else {
        throw new Error(result.error || 'Failed to create product');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to create product' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  console.log('🔍 MODAL DEBUG: AddProductModal rendering, isOpen:', isOpen);
  console.log('🔍 MODAL DEBUG: formData:', formData);

  return (
    <div className="admin-v2-modal-overlay" onClick={handleClose}>
      <div className="admin-v2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-v2-modal-header">
          <h2 className="admin-v2-modal-title">Add New Product</h2>
          <button
            className="admin-v2-modal-close-button"
            onClick={handleClose}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="admin-v2-modal-content">
          {/* Tab Navigation */}
          <div className="admin-v2-modal-tabs">
            <button
              type="button"
              className={`admin-v2-tab-button ${activeTab === 'single' ? 'active' : ''}`}
              onClick={() => setActiveTab('single')}
            >
              Single Entry
            </button>
            <button
              type="button"
              className={`admin-v2-tab-button ${activeTab === 'excel' ? 'active' : ''}`}
              onClick={() => setActiveTab('excel')}
            >
              Excel Import
            </button>
          </div>

          {activeTab === 'single' && (
            <form onSubmit={handleSubmit} className="admin-v2-product-form">
              <div className="admin-v2-form-row">
                <div className="admin-v2-form-group">
                  <label htmlFor="name" className="admin-v2-form-label">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={`admin-v2-form-input ${errors.name ? 'admin-v2-form-input-error' : ''}`}
                    placeholder="Enter product name"
                  />
                  {errors.name && <span className="admin-v2-form-error">{errors.name}</span>}
                </div>

                <div className="admin-v2-form-group">
                  <label htmlFor="brand" className="admin-v2-form-label">
                    Brand *
                  </label>
                  <input
                    type="text"
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                    className={`admin-v2-form-input ${errors.brand ? 'admin-v2-form-input-error' : ''}`}
                    placeholder="Enter brand name"
                  />
                  {errors.brand && <span className="admin-v2-form-error">{errors.brand}</span>}
                </div>
              </div>

              <div className="admin-v2-form-row">

                <div className="admin-v2-form-group">
                  <label htmlFor="amazon_asin" className="admin-v2-form-label">
                    Amazon ASIN
                  </label>
                  <input
                    type="text"
                    id="amazon_asin"
                    value={formData.amazon_asin}
                    onChange={(e) => handleInputChange('amazon_asin', e.target.value)}
                    className="admin-v2-form-input"
                    placeholder="Enter Amazon ASIN"
                  />
                </div>
              </div>

              <div className="admin-v2-form-row">
                <div className="admin-v2-form-group">
                  <label htmlFor="category" className="admin-v2-form-label">
                    Categories *
                  </label>
                  <CategorySelector
                    value={formData.category}
                    onChange={(categoryIds) => handleInputChange('category', categoryIds)}
                    language="tr"
                    multiple={true}
                    placeholder="Select categories..."
                    error={errors.category}
                  />
                  {errors.category && <span className="admin-v2-form-error">{errors.category}</span>}
                </div>

                <div className="admin-v2-form-group">
                  <label htmlFor="base_unit" className="admin-v2-form-label">
                    Base Unit *
                  </label>
                  <select
                    id="base_unit"
                    value={formData.base_unit}
                    onChange={(e) => handleInputChange('base_unit', e.target.value)}
                    className={`admin-v2-form-input ${errors.base_unit ? 'admin-v2-form-input-error' : ''}`}
                  >
                    {baseUnits.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                  {errors.base_unit && <span className="admin-v2-form-error">{errors.base_unit}</span>}
                </div>
              </div>

              <div className="admin-v2-form-row">
                <div className="admin-v2-form-group">
                  <label htmlFor="dimensions" className="admin-v2-form-label">
                    Size
                  </label>
                  <select
                    id="dimensions"
                    value={formData.dimensions}
                    onChange={(e) => handleInputChange('dimensions', e.target.value)}
                    className="admin-v2-form-input"
                  >
                    <option value="">Select Size (Optional)</option>
                    {sizeOptions.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                <div className="admin-v2-form-group">
                  <label htmlFor="color" className="admin-v2-form-label">
                    Color
                  </label>
                  <input
                    type="text"
                    id="color"
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    className="admin-v2-form-input"
                    placeholder="Enter color"
                  />
                </div>
              </div>

              <div className="admin-v2-form-group">
                <label htmlFor="description" className="admin-v2-form-label">
                  Description
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="admin-v2-form-input"
                  placeholder="Enter product description"
                  rows={3}
                />
              </div>

              {/* Company Specific Codes Section */}
              <div className="admin-v2-form-group">
                <label className="admin-v2-form-label">
                  {companyId ? `${companyName} Code (Required)` : 'Company Specific Codes (Optional)'}
                </label>
                <CompanySelector
                  value={selectedCompanies}
                  onChange={setSelectedCompanies}
                  placeholder="Select companies to add codes..."
                  multiple={!companyId} // Only allow multiple if no companyId is provided
                  disabled={!!companyId} // Disable if companyId is provided (auto-selected)
                />
                {companyId && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#1565c0'
                  }}>
                    <strong>{companyName}</strong> is automatically selected for this product
                  </div>
                )}
              </div>

              {selectedCompanies.length > 0 && (
                <div className="admin-v2-form-group" style={{ marginTop: '1rem' }}>
                  <label className="admin-v2-form-label">Company Codes</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {selectedCompanies.map((company) => (
                      <div key={company.company_id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ minWidth: '200px', fontSize: '13px' }}>
                          {company.company_name}:
                        </label>
                        <input
                          type="text"
                          value={companyCodes[company.company_id] || ''}
                          onChange={(e) => setCompanyCodes({
                            ...companyCodes,
                            [company.company_id]: e.target.value
                          })}
                          className="admin-v2-form-input"
                          placeholder="Enter company code..."
                          style={{ flex: 1 }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isAdmin && (
                <div className="admin-v2-form-group">
                  <label className="admin-v2-form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      className="admin-v2-form-checkbox"
                    />
                    <span className="admin-v2-form-checkbox-text">Active Product</span>
                  </label>
                </div>
              )}

              <div className="admin-v2-form-group">
                <label htmlFor="image_file" className="admin-v2-form-label">
                  Product Image
                </label>
                <input
                  type="file"
                  id="image_file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="admin-v2-form-input"
                  disabled={isSubmitting}
                />
                {formData.image_filename && (
                  <div className="admin-v2-image-preview">
                    <span>Selected: {formData.image_filename}</span>
                    <img
                      src={formData.image_file}
                      alt="Product preview"
                      style={{ maxWidth: '100px', maxHeight: '100px', marginTop: '8px' }}
                    />
                  </div>
                )}
                {errors.image_file && <span className="admin-v2-form-error">{errors.image_file}</span>}
              </div>

              {errors.submit && (
                <div className="admin-v2-form-error admin-v2-form-error-general">
                  {errors.submit}
                </div>
              )}

              {/* Variants Section */}
              <div className="admin-v2-variants-section">
                <div className="admin-v2-variants-header">
                  <h3>Product Variants (Optional)</h3>
                  <p className="admin-v2-variants-description">
                    Add color, size, and Amazon ASIN variants. They will share the same image and base information.
                  </p>
                </div>

                {variants.map((variant, index) => (
                  <div key={index} className="admin-v2-variant-item">
                    <div className="admin-v2-form-row">
                      <div className="admin-v2-form-group">
                        <label className="admin-v2-form-label">Color</label>
                        <input
                          type="text"
                          value={variant.color}
                          onChange={(e) => handleVariantChange(index, 'color', e.target.value)}
                          className="admin-v2-form-input"
                          placeholder="e.g., Red, Blue, Black"
                        />
                      </div>
                      <div className="admin-v2-form-group">
                        <label className="admin-v2-form-label">Size</label>
                        <select
                          value={variant.size}
                          onChange={(e) => handleVariantChange(index, 'size', e.target.value)}
                          className="admin-v2-form-input"
                        >
                          <option value="">Select Size</option>
                          {sizeOptions.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>
                      <div className="admin-v2-form-group">
                        <label className="admin-v2-form-label">Amazon ASIN</label>
                        <input
                          type="text"
                          value={variant.amazon_asin}
                          onChange={(e) => handleVariantChange(index, 'amazon_asin', e.target.value)}
                          className="admin-v2-form-input"
                          placeholder="e.g., B08N5WRWNW"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveVariant(index)}
                        className="admin-v2-variant-remove-button"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={handleAddVariant}
                  className="admin-v2-add-variant-button"
                >
                  + Add Variant
                </button>
              </div>

              <div className="admin-v2-modal-actions">
                <button
                  type="button"
                  onClick={handleClose}
                  className="admin-v2-modal-cancel-button"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-v2-modal-submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Product'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'excel' && (
            <div className="admin-v2-excel-section">
              <div className="admin-v2-excel-actions">
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="admin-v2-template-button"
                >
                  Download Template
                </button>
                <div className="admin-v2-file-input-container">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleExcelUpload}
                    className="admin-v2-file-input"
                    id="excel-upload"
                  />
                  <label htmlFor="excel-upload" className="admin-v2-upload-button">
                    Upload Excel File
                  </label>
                </div>
              </div>

              {showPreview && excelData.length > 0 && (
                <div className="admin-v2-preview-section">
                  <h3 className="admin-v2-preview-title">Preview Data ({excelData.length} products)</h3>

                  <div className="admin-v2-preview-table-container">
                    <table className="admin-v2-preview-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={selectedRows.size === excelData.length && excelData.length > 0}
                              onChange={handleSelectAll}
                              className="admin-v2-select-all-checkbox"
                            />
                          </th>
                          <th>Name</th>
                          <th>Brand</th>
                          <th>Category</th>
                          <th>Base Unit</th>
                          <th>Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {excelData.map((product, index) => (
                          <tr key={index}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedRows.has(index)}
                                onChange={() => handleSelectRow(index)}
                                className="admin-v2-row-checkbox"
                              />
                            </td>
                            <td>{product.name}</td>
                            <td>{product.brand}</td>
                            <td>{product.category}</td>
                            <td>{product.base_unit}</td>
                            <td>{product.is_active}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="admin-v2-preview-actions">
                    <div className="admin-v2-delete-controls">
                      <button
                        type="button"
                        onClick={handleDeleteSelected}
                        disabled={selectedRows.size === 0}
                        className="admin-v2-delete-selected-button"
                      >
                        Delete Selected ({selectedRows.size})
                      </button>
                    </div>
                  </div>

                  <div className="admin-v2-modal-actions">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="admin-v2-modal-cancel-button"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkCreate}
                      className="admin-v2-modal-submit-button"
                      disabled={isSubmitting || excelData.length === 0}
                    >
                      {isSubmitting ? 'Creating...' : `Create ${excelData.length} Products`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddProductModal;
